import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { onTurnSettled } from '../lib/durableRun';
import type { AgentNames } from '../types';
import { useRefreshThreads } from './useRefreshThreads';
import { isThreadTitlePending } from './useThreadTitleCache';

const THREAD_QUERY_KEY = ['AI', 'THREAD'];

/**
 * Hook to invalidate and refetch threads so sidebar shows updated list/titles.
 * @returns Mutation function to refresh threads
 */
export const useInvalidateThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ agentName, threadId }: { agentName: AgentNames; threadId: string }) => {
      await queryClient.invalidateQueries({ queryKey: [...THREAD_QUERY_KEY, agentName, threadId] });
    },
  });
};

/**
 * Cached thread history goes stale when a turn ends there, including one the user left mid-answer.
 * Only marked: the open chat already holds the answer, so the history is fetched on the next visit.
 */
export const useRefreshSettledThreads = () => {
  const queryClient = useQueryClient();
  const { mutate: refreshThreads } = useRefreshThreads();
  useEffect(
    () =>
      onTurnSettled((threadId, { kind, isLast }) => {
        // A thread left before its `data-thread-title` arrived still shows the fallback: the list has the real one now.
        if (kind === 'send' && isThreadTitlePending(queryClient, threadId)) refreshThreads();
        if (!isLast) return;
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) => queryKey[0] === THREAD_QUERY_KEY[0] && queryKey[1] === THREAD_QUERY_KEY[1] && queryKey[3] === threadId,
          refetchType: 'none',
        });
      }),
    [queryClient, refreshThreads]
  );
};
